/* eslint-disable react/jsx-props-no-spreading */
import {
  Box,
  Circle,
  Flex,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';

import CircleEditButton from '@/client/components/shared/buttons/CircleEditButton';
import type { ExpertFormData } from './types';
import ExpertsColorChangeIcon from '../ExpertsColorChangeIcon';
import { Pencil } from '@/client/components/icons/ContinuIcons';
import { SketchPicker } from 'react-color';
import { useExpertsStore } from '../expertsStore';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export default function ExpertGeneralDetailsForm() {
  const { t } = useTranslation();
  const { isEditingExpert: isEditing, setIsEditingExpert } = useExpertsStore();
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<ExpertFormData>();
  const { color } = watch();

  const hasNameError = !!errors.name;
  const hasDescriptionError = !!errors.description;

  return (
    <Flex alignItems="flex-start" gap={4}>
      <Popover placement="top-start">
        <PopoverTrigger>
          <Box
            paddingTop={1.5}
            position="relative"
            _hover={{ cursor: isEditing ? 'pointer' : 'default' }}
          >
            {isEditing && (
              <Circle
                size={4}
                zIndex={998}
                position="absolute"
                top={1}
                right={-1}
                backgroundColor="white"
                border="1px solid"
                borderColor="neutral.100"
              >
                <Pencil boxSize={2} color="neutral.900" />
              </Circle>
            )}

            <ExpertsColorChangeIcon boxSize={7} color={color} />
          </Box>
        </PopoverTrigger>

        {isEditing && (
          <PopoverContent
            backgroundColor="transparent"
            border="none"
            justifyContent="center"
            alignContent="center"
          >
            <PopoverBody display="flex" justifyContent="center">
              <SketchPicker
                disableAlpha
                color={color}
                onChangeComplete={(pickerColor) => {
                  setValue('color', pickerColor.hex, {
                    shouldValidate: false,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />
            </PopoverBody>
          </PopoverContent>
        )}
      </Popover>

      <Stack spacing={2} width="full">
        <Input
          {...register('name')}
          isReadOnly={!isEditing}
          variant="ghost"
          fontSize="24px"
          fontWeight={600}
          color="baseBlack"
          borderRadius="none"
          borderBottom={hasNameError ? '1px solid' : 'none'}
          borderBottomColor={hasNameError ? 'red.300' : 'transparent'}
          placeholder={`${t('admin.aiAgent.experts.form.generalDetails.namePlaceholder')}`}
          _placeholder={{
            color: 'neutral.500',
          }}
        />

        {hasNameError && (
          <Text fontSize="14px" fontWeight={500} color="red.300">
            {errors.name?.message as string}
          </Text>
        )}

        <Textarea
          {...register('description')}
          isReadOnly={!isEditing}
          resize="none"
          variant="ghost"
          width="full"
          placeholder={`${t('admin.aiAgent.experts.form.generalDetails.descriptionPlaceholder')}`}
          fontSize="14px"
          fontWeight={500}
          color="neutral.600"
          border={hasDescriptionError ? '1px solid' : 'none'}
          borderColor={hasDescriptionError ? 'red.300' : 'transparent'}
          _placeholder={{
            color: 'neutral.600',
          }}
        />

        {hasDescriptionError && (
          <Text fontSize="14px" fontWeight={500} color="red.300">
            {errors.description?.message as string}
          </Text>
        )}
      </Stack>

      {!isEditing && (
        <CircleEditButton colorScheme="neutral" onClick={() => setIsEditingExpert(true)} />
      )}
    </Flex>
  );
}
